import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../Api.js';
import './PopularDestinations.css';

const PopularDestinations = () => {
  const [popularDestinations, setPopularDestination] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [destinationName, setDestinationName] = useState('');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const scrollRef = useRef(null);

  // ── Fetch destinations ──
  useEffect(() => {
    const getDestinations = async () => {
      try {
        const response = await api.get('/destinations', {
          headers: {
            'X-API-TOKEN': '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5',
          },
        });
        if (response.data.status) {
          setPopularDestination(response.data.data);
        }
      } catch (error) {
        console.log('API Error:', error);
      } finally {
        setLoading(false);
      }
    };
    getDestinations();
  }, []);

  // ── Scroll handling ──
  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -360 : 360;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // ── YouTube helpers ──
  const extractYouTubeId = (url) => {
    if (!url) return null;
    // Handle youtube.com/watch?v=...
    const regExp = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?#]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const getYouTubeEmbedUrl = (url, autoplay = false) => {
    const id = extractYouTubeId(url);
    if (!id) return null;
    return `https://www.youtube.com/embed/${id}${autoplay ? '?autoplay=1&rel=0' : '?rel=0'}`;
  };

  const getYouTubeThumbnail = (url) => {
    const id = extractYouTubeId(url);
    if (!id) return null;
    return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
  };

  // ── Lightbox handlers ──
  const openLightbox = async (destination) => {
    setIsFetching(true);
    setDestinationName(destination.name);
    setZoomLevel(1);
    setIsVideoPlaying(false);

    try {
      const response = await api.get(`/destinations/${destination.id}`, {
        headers: {
          'X-API-TOKEN': '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5',
        },
      });

      const data = response.data.data;
      const cover = destination.cover_photo || '';
      const photos = data.photos || [];
      const videos = data.videos || [];

      // Build unified media array
      const items = [];

      // Add cover photo
      if (cover) {
        items.push({ type: 'image', url: cover, title: 'Cover' });
      }

      // Add photos
      photos.forEach((url) => {
        if (url) items.push({ type: 'image', url });
      });

      // Add videos
      videos.forEach((url) => {
        if (url) items.push({ type: 'video', url });
      });

      setMediaItems(items);
      setCurrentIndex(0);
      setLightboxOpen(true);
    } catch (error) {
      console.error('Failed to fetch destination details:', error);
      // Fallback: show only the cover photo
      const cover = destination.cover_photo || '';
      if (cover) {
        setMediaItems([{ type: 'image', url: cover, title: 'Cover' }]);
        setCurrentIndex(0);
        setLightboxOpen(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setMediaItems([]);
    setZoomLevel(1);
    setIsVideoPlaying(false);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
    setZoomLevel(1);
    setIsVideoPlaying(false);
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
    setZoomLevel(1);
    setIsVideoPlaying(false);
  };

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
  };

  // ── Keyboard events ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, mediaItems]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? 'hidden' : '';
    return () => (document.body.style.overflow = '');
  }, [lightboxOpen]);

  // ── Render media item ──
  const renderMediaItem = (item, index) => {
    if (index !== currentIndex) return null;

    if (item.type === 'video') {
      const embedUrl = getYouTubeEmbedUrl(item.url, isVideoPlaying);
      const thumbnail = getYouTubeThumbnail(item.url);

      return (
        <div className="lightbox-media-wrapper lightbox-video-wrapper">
          {isVideoPlaying && embedUrl ? (
            <iframe
              src={embedUrl}
              className="lightbox-video-iframe"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="lightbox-video-thumbnail" onClick={handlePlayVideo}>
              {thumbnail ? (
                <img src={thumbnail} alt="Video thumbnail" className="lightbox-video-thumb-img" />
              ) : (
                <div className="lightbox-video-placeholder">
                  <span>🎬</span>
                </div>
              )}
              <div className="lightbox-play-button">
                <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                  <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.6)" />
                  <polygon points="10,7 17,12 10,17" fill="#fff" />
                </svg>
              </div>
              <span className="lightbox-video-label">▶ Click to play</span>
            </div>
          )}
        </div>
      );
    }

    // Image
    return (
      <div className="lightbox-media-wrapper lightbox-image-wrapper">
        <img
          src={item.url}
          alt={item.title || `Image ${index + 1}`}
          className="lightbox-image"
          style={{ transform: `scale(${zoomLevel})` }}
          draggable={false}
        />
      </div>
    );
  };

  // ── Render ──
  if (loading) {
    return <div className="destinations-loading">Loading Destinations...</div>;
  }

  const currentItem = mediaItems[currentIndex] || {};
  const isImage = currentItem.type === 'image';
  const totalItems = mediaItems.length;

  return (
    <section className="destinations-section">
      <div className="destinations-header">
        <h2 className="destinations-title">Popular Destinations</h2>
        <div className="slider-controls">
          <button
            className="control-btn"
            onClick={() => handleScroll('left')}
            aria-label="Scroll left"
          >
            &#10094;
          </button>
          <button
            className="control-btn"
            onClick={() => handleScroll('right')}
            aria-label="Scroll right"
          >
            &#10095;
          </button>
        </div>
      </div>

      <div className="destinations-grid-wrapper" ref={scrollRef}>
        <div className="destinations-grid">
          {popularDestinations.map((item) => {
            const citiesList = item.cities?.slice(0, 3).map((c) => c.name).join(', ');
            return (
              <div
                key={item.id}
                className="destination-card"
                onClick={() => openLightbox(item)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-image-wrapper">
                  <img
                    src={item.cover_photo}
                    alt={item.name}
                    className="card-image"
                    loading="lazy"
                  />
                  <div className="card-overlay" />
                </div>

                <div className="card-content">
                  <div className="icon-badge">
                    <svg
                      viewBox="0 0 24 24"
                      className="location-icon"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 21s-6-5.33-6-10a6 6 0 0 1 12 0c0 4.67-6 10-6 10z" />
                      <circle cx="12" cy="11" r="2" />
                    </svg>
                  </div>
                  <h3 className="card-title">{item.name}</h3>
                  {citiesList && (
                    <p className="card-subtitle">
                      {citiesList}
                      {item.cities.length > 3 ? '...' : ''}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Lightbox Modal ──────────────────────────────────────────── */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="lightbox-close" onClick={closeLightbox}>
              &times;
            </button>

            {/* Navigation arrows */}
            {totalItems > 1 && (
              <>
                <button className="lightbox-nav lightbox-nav-prev" onClick={goPrev}>
                  &#10094;
                </button>
                <button className="lightbox-nav lightbox-nav-next" onClick={goNext}>
                  &#10095;
                </button>
              </>
            )}

            {/* Media content */}
            <div className="lightbox-image-container">
              {isFetching ? (
                <div className="lightbox-loader">Loading media...</div>
              ) : (
                renderMediaItem(currentItem, currentIndex)
              )}
            </div>

            {/* Bottom controls */}
            <div className="lightbox-controls">
              <div className="lightbox-left-controls">
                <div className="lightbox-counter">
                  {totalItems > 0 && `${currentIndex + 1} / ${totalItems}`}
                  {currentItem.type === 'video' && (
                    <span className="lightbox-media-badge">🎬 Video</span>
                  )}
                  {currentItem.type === 'image' && (
                    <span className="lightbox-media-badge">📷 Photo</span>
                  )}
                </div>
              </div>

              <div className="lightbox-right-controls">
                {isImage && (
                  <div className="lightbox-zoom-controls">
                    <button onClick={handleZoomOut} title="Zoom Out">−</button>
                    <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                    <button onClick={handleZoomIn} title="Zoom In">+</button>
                    <button onClick={handleResetZoom} title="Reset Zoom">⟲</button>
                  </div>
                )}
                <button
                  className="lightbox-packages-btn"
                  onClick={() => {
                    window.location.href = `/honeymoon/packages?location=${encodeURIComponent(
                      destinationName
                    )}`;
                  }}
                >
                  View All Packages
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PopularDestinations;